// src/components/views/NFTAnalyticsView.js

import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Accordion, Table, Spinner, Button, Alert } from 'react-bootstrap';
import moment from 'moment';
import { ethers } from 'ethers';
import { useLocation, useNavigate } from 'react-router-dom';

import { addressToSogniUsername } from '../../utils/addressToSogniUsername';
import { getApiBaseUrl } from '../../utils/apiConfig';
import { getTokenImageUrl } from '../../utils/tokenImage';
import V7Abi from '../../contracts/Prospect2WorkerLicenseV7.json';

// Fallback denominator for "Total NFTs in Collection" when the live maxSupply()
// read fails. maxSupply is raised on-chain over time, so the env value drifts.
const ENV_MAX_SUPPLY =
  process.env.REACT_APP_ACTUAL_MAX_SUPPLY || process.env.REACT_APP_INITIAL_MAX_SUPPLY || 0;

// Combined Performance Analytics Component
const PerformanceAnalytics = ({ dailyData, yesterdayData, lifetimeData }) => {
  return (
    <div style={{
      backgroundColor: 'rgba(255, 255, 255, 0.03)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '12px',
      padding: '24px'
    }}>
      {/* Title and Column Headers */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: '150px 1fr 1fr 1fr', 
        gap: '12px',
        marginBottom: '16px',
        paddingBottom: '12px',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        <div style={{ 
          color: '#9fef00', 
          alignSelf: 'center'
        }}>
          Performance Analytics
        </div>
        <div style={{ 
          color: '#9fef00', 
          fontSize: '0.9rem', 
          textAlign: 'center'
        }}>
          UTC Day
        </div>
        <div style={{ 
          color: '#9fef00', 
          fontSize: '0.9rem', 
          textAlign: 'center'
        }}>
          UTC Yesterday
        </div>
        <div style={{ 
          color: '#9fef00', 
          fontSize: '0.9rem', 
          textAlign: 'center'
        }}>
          Lifetime
        </div>
      </div>

      {/* Metrics */}
      <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: '12px' }}>
        <div style={{ display: 'grid', gap: '12px' }}>
          <div style={{ color: '#999', fontSize: '0.9rem', paddingTop: '8px' }}>Jobs Accepted</div>
          <div style={{ color: '#999', fontSize: '0.9rem', paddingTop: '8px' }}>Jobs Completed</div>
          <div style={{ color: '#999', fontSize: '0.9rem', paddingTop: '8px' }}>Jobs Failed</div>
          <div style={{ color: '#999', fontSize: '0.9rem', paddingTop: '8px' }}>Model Switches</div>
          <div style={{ color: '#999', fontSize: '0.9rem', paddingTop: '8px' }}>SOGNI Tokens</div>
          <div style={{ color: '#999', fontSize: '0.9rem', paddingTop: '8px' }}>USD Value</div>
          <div style={{ color: '#999', fontSize: '0.9rem', paddingTop: '8px' }}>Spark Value</div>
        </div>
        <div style={{ display: 'grid', gap: '12px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
            <div style={{ fontSize: '1.1rem', fontFamily: 'monospace', textAlign: 'center' }}>
              {parseInt(dailyData?.jobAcceptedWorker || 0).toLocaleString()}
            </div>
            <div style={{ fontSize: '1.1rem', fontFamily: 'monospace', textAlign: 'center' }}>
              {parseInt(yesterdayData?.jobAcceptedWorker || 0).toLocaleString()}
            </div>
            <div style={{ fontSize: '1.1rem', fontFamily: 'monospace', textAlign: 'center' }}>
              {parseInt(lifetimeData?.jobAcceptedWorker || 0).toLocaleString()}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
            <div style={{ fontSize: '1.1rem', fontFamily: 'monospace', textAlign: 'center' }}>
              {parseInt(dailyData?.jobCompleteWorker || 0).toLocaleString()}
            </div>
            <div style={{ fontSize: '1.1rem', fontFamily: 'monospace', textAlign: 'center' }}>
              {parseInt(yesterdayData?.jobCompleteWorker || 0).toLocaleString()}
            </div>
            <div style={{ fontSize: '1.1rem', fontFamily: 'monospace', textAlign: 'center' }}>
              {parseInt(lifetimeData?.jobCompleteWorker || 0).toLocaleString()}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
            <div style={{ fontSize: '1.1rem', fontFamily: 'monospace', textAlign: 'center', color: '#ff4444' }}>
              {parseInt(dailyData?.jobErrorWorker || 0).toLocaleString()}
            </div>
            <div style={{ fontSize: '1.1rem', fontFamily: 'monospace', textAlign: 'center', color: '#ff4444' }}>
              {parseInt(yesterdayData?.jobErrorWorker || 0).toLocaleString()}
            </div>
            <div style={{ fontSize: '1.1rem', fontFamily: 'monospace', textAlign: 'center', color: '#ff4444' }}>
              {parseInt(lifetimeData?.jobErrorWorker || 0).toLocaleString()}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
            <div style={{ fontSize: '1.1rem', fontFamily: 'monospace', textAlign: 'center', color: '#ff4444' }}>
              {parseInt(dailyData?.initiatingModelWorker || 0).toLocaleString()}
            </div>
            <div style={{ fontSize: '1.1rem', fontFamily: 'monospace', textAlign: 'center', color: '#ff4444' }}>
              {parseInt(yesterdayData?.initiatingModelWorker || 0).toLocaleString()}
            </div>
            <div style={{ fontSize: '1.1rem', fontFamily: 'monospace', textAlign: 'center', color: '#ff4444' }}>
              {parseInt(lifetimeData?.initiatingModelWorker || 0).toLocaleString()}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
            <div style={{ fontSize: '1.1rem', fontFamily: 'monospace', textAlign: 'center' }}>
              {parseFloat(dailyData?.renderEarnSogniWorker || 0).toLocaleString(undefined, { maximumFractionDigits: 3 })}
            </div>
            <div style={{ fontSize: '1.1rem', fontFamily: 'monospace', textAlign: 'center' }}>
              {parseFloat(yesterdayData?.renderEarnSogniWorker || 0).toLocaleString(undefined, { maximumFractionDigits: 3 })}
            </div>
            <div style={{ fontSize: '1.1rem', fontFamily: 'monospace', textAlign: 'center' }}>
              {parseFloat(lifetimeData?.renderEarnSogniWorker || 0).toLocaleString(undefined, { maximumFractionDigits: 3 })}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
            <div style={{ fontSize: '1.1rem', fontFamily: 'monospace', textAlign: 'center' }}>
              ${parseFloat(dailyData?.renderUSDCompleteWorker || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <div style={{ fontSize: '1.1rem', fontFamily: 'monospace', textAlign: 'center' }}>
              ${parseFloat(yesterdayData?.renderUSDCompleteWorker || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <div style={{ fontSize: '1.1rem', fontFamily: 'monospace', textAlign: 'center' }}>
              ${parseFloat(lifetimeData?.renderUSDCompleteWorker || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
            <div style={{ fontSize: '1.1rem', fontFamily: 'monospace', textAlign: 'center' }}>
              {parseFloat(dailyData?.renderTokenCompleteWorker || 0).toLocaleString(undefined, { maximumFractionDigits: 3 })}
            </div>
            <div style={{ fontSize: '1.1rem', fontFamily: 'monospace', textAlign: 'center' }}>
              {parseFloat(yesterdayData?.renderTokenCompleteWorker || 0).toLocaleString(undefined, { maximumFractionDigits: 3 })}
            </div>
            <div style={{ fontSize: '1.1rem', fontFamily: 'monospace', textAlign: 'center' }}>
              {parseFloat(lifetimeData?.renderTokenCompleteWorker || 0).toLocaleString(undefined, { maximumFractionDigits: 3 })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const NFTAnalyticsView = ({ account, publicContract, sogniClient }) => {
  const location = useLocation();
  // The URL as it is when the worker list loads, read without making the load depend on it.
  const locationSearchRef = useRef(location.search);
  locationSearchRef.current = location.search;
  const navigate = useNavigate();
  // The baseUrl for your NFT API
  const baseUrl = getApiBaseUrl();

  const [activeKey, setActiveKey] = useState(null);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth <= 1200);

  // Handle screen resize
  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth <= 1200);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle initial deep linking and nftId parameter
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const section = params.get('section'); // 'platform' or 'nft'
    
    // Set initial activeKey based on deep link
    if (section) {
      switch (section) {
        case 'platform':
          setActiveKey('0');
          break;
        case 'nft':
          setActiveKey('1');
          break;
        default:
          break;
      }
    }
  }, [location]);

  //----------------------------------------------------------------------
  // Utility to unify "object or array" responses into an array
  //----------------------------------------------------------------------
  function parseNftObjectOrArray(maybeObj) {
    if (Array.isArray(maybeObj)) {
      return maybeObj;
    }
    // If it's an object: { "4": {owner:"0x...", ...}, "5": { ...} }
    return Object.entries(maybeObj).map(([tokenId, details]) => ({
      tokenId: parseInt(tokenId, 10),
      // If the server includes a minted flag, use it; otherwise assume minted if "owner" is non-null
      minted: !!details.owner,
      ...details,
    }));
  }

  //----------------------------------------------------------------------
  // Account Balance State and Functions
  //----------------------------------------------------------------------
  const [balanceData, setBalanceData] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [balanceError, setBalanceError] = useState('');

  // Function to fetch account balance
  const fetchAccountBalance = async () => {
    setBalanceLoading(true);
    setBalanceError('');
    
    try {
      // Cookie-based auth: rely on session cookie, no bearer tokens

      const response = await fetch('https://api.sogni.ai/v3/account/balance', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Balance API error:', response.status, errorText);
        throw new Error(`Failed to fetch balance: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      if (data.status === 'success' && data.data) {
        setBalanceData(data.data);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Error fetching account balance:', error);
      setBalanceError(error.message);
    } finally {
      setBalanceLoading(false);
    }
  };

  //----------------------------------------------------------------------
  // My GPU Worker Stats (panel #0)
  //----------------------------------------------------------------------
  const [gpuLoading, setGpuLoading] = useState(false);
  const [gpuError, setGpuError] = useState('');
  const [gpuData, setGpuData] = useState(null);
  const [firstNftId, setFirstNftId] = useState(null);
  const [allWorkers, setAllWorkers] = useState([]);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [formattedTimes, setFormattedTimes] = useState({});
  const [nftAnalytics, setNftAnalytics] = useState({
    daily: null,
    yesterday: null,
    lifetime: null
  });

  const [jobHistory, setJobHistory] = useState([]);
  const [jobHistoryLoading, setJobHistoryLoading] = useState(false);
  const [jobHistoryError, setJobHistoryError] = useState('');

  // Add state for pagination
  const [jobOffset, setJobOffset] = useState(0);
  const [hasMoreJobs, setHasMoreJobs] = useState(true);

  useEffect(() => {
    if (activeKey === '0') {
      setGpuLoading(true);
      setGpuError('');

      if (!account) {
        setGpuError('No wallet connected.');
        setGpuLoading(false);
        return;
      }

      const fetchMyNfts = async () => {
        try {
          console.log('📡 Fetching NFTs for account:', account);
          const res = await fetch(`${baseUrl}/nfts?owner=${account}`);
          if (!res.ok) {
            throw new Error('Failed to fetch your NFTs.');
          }
          const rawData = await res.json();
          const data = parseNftObjectOrArray(rawData);

          if (data.length === 0) {
            setGpuError('No NFTs found.');
            setGpuLoading(false);
            return;
          }

          const minted = data.filter((n) => n.minted === true);
          if (minted.length === 0) {
            setGpuError('No minted NFTs found.');
            setGpuLoading(false);
            return;
          }

          console.log('🔄 Fetching status for', minted.length, 'minted NFTs');
          // Fetch status for all minted NFTs
          const workerPromises = minted.map(async (nft) => {
            try {
              const res2 = await fetch(
                `https://socket.sogni.ai/api/v1/client/nft/${nft.tokenId}/status`
              );
              const text = await res2.text();
              if (text.trim().startsWith('No client connected')) {
                return null;
              }
              let parsed;
              try {
                parsed = JSON.parse(text);
              } catch (err) {
                return null;
              }
              if (typeof parsed.id === 'undefined' && typeof parsed.lastSeen === 'undefined') {
                return null;
              }
              return {
                nftId: nft.tokenId,
                data: parsed
              };
            } catch (err) {
              console.error(`Failed to fetch status for NFT ${nft.tokenId}:`, err);
              return null;
            }
          });

          const workers = (await Promise.all(workerPromises)).filter(Boolean);
          console.log('✅ Got worker data for', workers.length, 'NFTs');
          setAllWorkers(workers);

          // Handle worker selection based on URL or default to first worker
          const params = new URLSearchParams(locationSearchRef.current);
          const nftId = params.get('nftId');
          
          if (nftId) {
            const worker = workers.find(w => w.nftId === parseInt(nftId));
            if (worker) {
              console.log('🎯 Selected worker from URL:', worker.nftId);
              setSelectedWorker(worker);
              setFirstNftId(worker.nftId);
              setGpuData(worker.data);
            } else {
              // NFT ID not found in owned list, try to fetch it directly (for debugging non-owned NFTs)
              console.log('🔍 NFT ID not in owned list, fetching directly:', nftId);
              try {
                const directRes = await fetch(`https://socket.sogni.ai/api/v1/client/nft/${nftId}/status`);
                const directText = await directRes.text();
                
                if (directText.trim().startsWith('No client connected')) {
                  console.log('❌ Non-owned NFT never connected:', nftId);
                  setGpuError(`NFT #${nftId} has never connected to the Sogni network.`);
                  setFirstNftId(parseInt(nftId));
                  setGpuData(null);
                } else {
                  try {
                    const directParsed = JSON.parse(directText);
                    console.log('✅ Successfully fetched non-owned NFT:', nftId);
                    setSelectedWorker(null); // No worker button for non-owned NFTs
                    setFirstNftId(parseInt(nftId));
                    setGpuData(directParsed);
                  } catch (parseErr) {
                    console.log('❌ Failed to parse non-owned NFT data:', nftId);
                    setGpuError(`Failed to load data for NFT #${nftId}.`);
                    setFirstNftId(parseInt(nftId));
                    setGpuData(null);
                  }
                }
              } catch (directErr) {
                console.error('Error fetching non-owned NFT:', directErr);
                setGpuError(`Failed to fetch data for NFT #${nftId}.`);
                setFirstNftId(parseInt(nftId));
                setGpuData(null);
              }
            }
          } else if (workers.length > 0) {
            // No worker specified in URL, use first one
            const firstWorker = workers[0];
            console.log('🆕 No URL worker, using first:', firstWorker.nftId);
            setSelectedWorker(firstWorker);
            setFirstNftId(firstWorker.nftId);
            setGpuData(firstWorker.data);
          }

          // Fetch account balance when GPU stats are loaded
          console.log('💰 Fetching account balance');
          fetchAccountBalance();

          setGpuLoading(false);
        } catch (error) {
          console.error('Error fetching NFTs:', error);
          setGpuError(error.message);
          setGpuLoading(false);
        }
      };

      fetchMyNfts();
    }
  // Not location.search: selecting a worker writes ?nftId= and must not reload every
  // worker's status (the deep-link effect below handles the selection).
  }, [activeKey, account, baseUrl]);

  // Memoize formattedTimes to prevent re-calc on every render
  const formattedTimesMemo = useMemo(() => {
    if (!gpuData) return {};

    const times = {};
    // Worker status times
    if (gpuData.connectTime) times.connectTime = calculateRelativeTime(gpuData.connectTime);
    if (gpuData.lastAppStartTime) times.lastAppStartTime = calculateRelativeTime(gpuData.lastAppStartTime);
    if (gpuData.lastJobCompleteTime) times.lastJobCompleteTime = calculateRelativeTime(gpuData.lastJobCompleteTime);
    if (gpuData.lastSeen) times.lastSeen = calculateDurationSince(gpuData.lastSeen);
    if (gpuData.lastModelSwitchTime) times.lastModelSwitchTime = calculateRelativeTime(gpuData.lastModelSwitchTime);
    
    // Active job times - only calculate if activeWorkerJob exists and has valid timestamps
    if (gpuData.activeWorkerJob) {
      if (gpuData.activeWorkerJob.actualStartTime) {
        times.activeJobStart = calculateRelativeTime(gpuData.activeWorkerJob.actualStartTime);
      } else {
        times.activeJobStart = calculateRelativeTime(gpuData.activeWorkerJob.startTime);
      }
      if (gpuData.activeWorkerJob.updateTime) {
        times.activeJobUpdate = calculateRelativeTime(gpuData.activeWorkerJob.updateTime);
      }
    }
    
    // Error times
    if (gpuData.lastJobTimeoutTime) times.lastJobTimeoutTime = calculateRelativeTime(gpuData.lastJobTimeoutTime);
    if (gpuData.lastTestJobTimeoutTime) times.lastTestJobTimeoutTime = calculateRelativeTime(gpuData.lastTestJobTimeoutTime);
    if (gpuData.lastWorkerKickTime) times.lastWorkerKickTime = calculateRelativeTime(gpuData.lastWorkerKickTime);
    
    return times;
  }, [gpuData]);

  useEffect(() => {
    setFormattedTimes(formattedTimesMemo);
  }, [formattedTimesMemo]);

  // Simple debounce function
  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  };

  // Update refreshGpuStats to handle multiple workers (with debounce)
  const debouncedRefreshGpuStats = debounce(async () => {
    console.log('🔄 debouncedRefreshGpuStats called');
    if (!account) {
      setGpuError('No wallet connected.');
      return;
    }

    setGpuLoading(true);
    setGpuError('');

    try {
      console.log('📡 Refresh: Fetching NFTs for account:', account);
      const res = await fetch(`${baseUrl}/nfts?owner=${account}`);
      if (!res.ok) {
        throw new Error('Failed to fetch your NFTs.');
      }
      const rawData = await res.json();
      const data = parseNftObjectOrArray(rawData);

      if (data.length === 0) {
        setGpuError('No NFTs found.');
        setGpuLoading(false);
        return;
      }

      const minted = data.filter((n) => n.minted === true);
      if (minted.length === 0) {
        setGpuError('No minted NFTs found.');
        setGpuLoading(false);
        return;
      }

      console.log('🔄 Refresh: Fetching status for', minted.length, 'minted NFTs');
      const workerPromises = minted.map(async (nft) => {
        try {
          const res2 = await fetch(
            `https://socket.sogni.ai/api/v1/client/nft/${nft.tokenId}/status`
          );
          const text = await res2.text();
          if (text.trim().startsWith('No client connected')) {
            return null;
          }
          let parsed;
          try {
            parsed = JSON.parse(text);
          } catch (err) {
            return null;
          }
          if (typeof parsed.id === 'undefined' && typeof parsed.lastSeen === 'undefined') {
            return null;
          }
          return {
            nftId: nft.tokenId,
            data: parsed
          };
        } catch (err) {
          console.error(`Failed to fetch status for NFT ${nft.tokenId}:`, err);
          return null;
        }
      });

      const workers = (await Promise.all(workerPromises)).filter(w => w !== null);
      console.log('✅ Refresh: Got worker data for', workers.length, 'NFTs');
      setAllWorkers(workers);
      
      if (workers.length > 0) {
        if (selectedWorker) {
          const updatedWorker = workers.find(w => w.nftId === selectedWorker.nftId);
          if (updatedWorker) {
            // Transform the data to ensure timestamps are valid
            if (updatedWorker.data.activeWorkerJob) {
              // Only set timestamps if they don't exist or are invalid
              if (!updatedWorker.data.activeWorkerJob.actualStartTime || 
                  updatedWorker.data.activeWorkerJob.actualStartTime === 'undefined' || 
                  updatedWorker.data.activeWorkerJob.actualStartTime === 'null') {
                updatedWorker.data.activeWorkerJob.actualStartTime = Date.now();
              }
              if (!updatedWorker.data.activeWorkerJob.updateTime || 
                  updatedWorker.data.activeWorkerJob.updateTime === 'undefined' || 
                  updatedWorker.data.activeWorkerJob.updateTime === 'null') {
                updatedWorker.data.activeWorkerJob.updateTime = Date.now();
              }
            }
            // Only update selectedWorker if the data has actually changed
            setGpuData(updatedWorker.data);
          } else {
            // Selected worker no longer exists, fall back to first worker
            const fallbackWorker = workers[0];
            // Transform the data to ensure timestamps are valid
            if (fallbackWorker.data.activeWorkerJob) {
              if (!fallbackWorker.data.activeWorkerJob.actualStartTime || 
                  fallbackWorker.data.activeWorkerJob.actualStartTime === 'undefined' || 
                  fallbackWorker.data.activeWorkerJob.actualStartTime === 'null') {
                fallbackWorker.data.activeWorkerJob.actualStartTime = Date.now();
              }
              if (!fallbackWorker.data.activeWorkerJob.updateTime || 
                  fallbackWorker.data.activeWorkerJob.updateTime === 'undefined' || 
                  fallbackWorker.data.activeWorkerJob.updateTime === 'null') {
                fallbackWorker.data.activeWorkerJob.updateTime = Date.now();
              }
            }
            setSelectedWorker(fallbackWorker);
            setFirstNftId(fallbackWorker.nftId);
            setGpuData(fallbackWorker.data);
          }
        } else {
          // No worker selected, select the first one
          const firstWorker = workers[0];
          // Transform the data to ensure timestamps are valid
          if (firstWorker.data.activeWorkerJob) {
            if (!firstWorker.data.activeWorkerJob.actualStartTime || 
                firstWorker.data.activeWorkerJob.actualStartTime === 'undefined' || 
                firstWorker.data.activeWorkerJob.actualStartTime === 'null') {
              firstWorker.data.activeWorkerJob.actualStartTime = Date.now();
            }
            if (!firstWorker.data.activeWorkerJob.updateTime || 
                firstWorker.data.activeWorkerJob.updateTime === 'undefined' || 
                firstWorker.data.activeWorkerJob.updateTime === 'null') {
              firstWorker.data.activeWorkerJob.updateTime = Date.now();
            }
          }
          setSelectedWorker(firstWorker);
          setFirstNftId(firstWorker.nftId);
          setGpuData(firstWorker.data);
        }

        // Refresh account balance when workers are updated
        console.log('💰 Refresh: Fetching account balance');
        fetchAccountBalance();

        // Note: Removed NFT analytics fetch from here as it's handled by dedicated useEffect
      } else {
        setGpuError('No active workers found.');
      }
    } catch (err) {
      console.error('GPU Worker Stats fetch failed:', err);
      setGpuError(err.message);
    } finally {
      setGpuLoading(false);
    }
  }, 500);  // 500ms debounce

  const refreshGpuStats = () => debouncedRefreshGpuStats();

  // Add this helper to format timestamps for display
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    return moment(timestamp).format('YYYY-MM-DD HH:mm:ss');
  };

  // Add function to load more jobs
  const loadMoreJobs = async (nftId) => {
    if (!nftId) return;
    setJobHistoryLoading(true);
    try {
      const nextOffset = jobOffset + 10;
      const res = await fetch(`https://api.sogni.ai/v1/jobs/list?nftTokenId=${nftId}&state=completed&limit=10&offset=${nextOffset}`);
      if (!res.ok) throw new Error('Failed to fetch job history');
      const data = await res.json();
      
      setHasMoreJobs(!!data.data.next);
      setJobHistory(prevJobs => [...prevJobs, ...(data.data.jobs || [])]);
      setJobOffset(nextOffset);
    } catch (err) {
      console.error('Job history fetch error:', err);
      setJobHistoryError(err.message);
    } finally {
      setJobHistoryLoading(false);
    }
  };

  // Update the original fetchJobHistory to reset pagination
  const fetchJobHistory = async (nftId) => {
    console.log('📋 fetchJobHistory called for NFT:', nftId);
    if (!nftId) return;
    setJobHistoryLoading(true);
    setJobHistoryError('');
    try {
      const res = await fetch(`https://api.sogni.ai/v1/jobs/list?nftTokenId=${nftId}&state=completed&limit=10`);
      if (!res.ok) throw new Error('Failed to fetch job history');
      const data = await res.json();
      setJobHistory(data.data.jobs || []);
      setHasMoreJobs(!!data.data.next);
      setJobOffset(0);
      console.log('✅ Job history loaded:', data.data.jobs?.length || 0, 'jobs');
    } catch (err) {
      console.error('Job history fetch error:', err);
      setJobHistoryError(err.message);
    } finally {
      setJobHistoryLoading(false);
    }
  };

  // NFT thumbnail
  const nftThumbnailUrl = firstNftId ? getTokenImageUrl(firstNftId) : '';

  //----------------------------------------------------------------------
  // Platform Analytics (panel #1)
  //----------------------------------------------------------------------
  const [platformLoading, setPlatformLoading] = useState(false);
  const [platformError, setPlatformError] = useState('');
  const [platformData, setPlatformData] = useState(null);

  useEffect(() => {
    if (activeKey === '0') {
      setPlatformLoading(true);
      setPlatformError('');
      setPlatformData(null);

      const fetchPlatformAnalytics = async () => {
        try {
          const response = await fetch(
            'https://api.sogni.ai/v1/analytics/lifetime?keys=usignupAddress,jobCompleteWorker,uloginWorkerAddress'
          );
          if (!response.ok) {
            throw new Error('Failed to fetch platform analytics');
          }
          const data = await response.json();
          setPlatformData(data);
        } catch (err) {
          console.error('Platform analytics error:', err);
          setPlatformError(err.message);
        } finally {
          setPlatformLoading(false);
        }
      };

      fetchPlatformAnalytics();
    } else {
      setPlatformLoading(false);
      setPlatformError('');
      setPlatformData(null);
    }
  }, [activeKey]);

  const formatNumber = (val) => {
    if (!val) return 'N/A';
    const num = parseFloat(val);
    if (isNaN(num)) return val;
    return num.toLocaleString();
  };

  const formatJobCount = (id, imageCount) => {
    if (!id) return 'N/A';
    const parts = id.split('-');
    return `${parts[0]}... image ${Number(parts[parts.length - 1]) + 1} of ${imageCount}`;
  };

  const formatSpeed = (speed) => {
    if (!speed) return 'N/A';
    return `${parseFloat(speed).toFixed(2)}x baseline`;
  };

  const getCooldownTimeLeft = (timestamp) => {
    if (!timestamp) return null;
    const now = Date.now();
    const cooldownEnd = parseInt(timestamp, 10);
    if (cooldownEnd <= now) return null;
    
    const diffMs = cooldownEnd - now;
    const duration = moment.duration(diffMs);
    const days = Math.floor(duration.asDays());
    const hours = Math.floor(duration.asHours()) % 24;
    const minutes = Math.floor(duration.asMinutes()) % 60;
    const seconds = Math.floor(duration.asSeconds()) % 60;

    const parts = [];
    if (days > 0) parts.push(`${days} day${days !== 1 ? 's' : ''}`);
    if (hours > 0) parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
    if (minutes > 0) parts.push(`${minutes} min${minutes !== 1 ? 's' : ''}`);
    if (seconds > 0) parts.push(`${seconds} sec${seconds !== 1 ? 's' : ''}`);

    // Only take the first two parts
    return parts.slice(0, 2).join(', ');
  };

  //----------------------------------------------------------------------
  // NFT Analytics (panel #2) + Refresh
  //----------------------------------------------------------------------
  const [nftLoading, setNftLoading] = useState(false);
  const [nftError, setNftError] = useState('');
  const [nftData, setNftData] = useState({
    totalTokens: ENV_MAX_SUPPLY,
    mintedCount: 0,
    lastMinted: [],
    lastTransfers: [],
    contractAddress: process.env.REACT_APP_DEPLOYED_NFT_CONTRACT || '',
  });

  // We read the actual totalSupply and the live cap from the contract here:
  // This function is used inside fetchNftAnalytics below.
  // maxSupply() is only on the V7 ABI, so rebind publicContract's address and
  // provider to it rather than relying on whatever ABI the caller passed in.
  async function fetchContractSupply() {
    if (!publicContract) return { mintedCount: 0, maxSupply: ENV_MAX_SUPPLY };

    // If your contract's `totalSupply` returns a BigNumber, convert to a normal number
    let mintedCount = 0;
    try {
      const supplyBN = await publicContract.totalSupply();
      mintedCount = supplyBN.toNumber();
    } catch (error) {
      console.error('Error fetching totalSupply from contract:', error);
    }

    let maxSupply = ENV_MAX_SUPPLY;
    try {
      const v7 = new ethers.Contract(
        publicContract.address,
        V7Abi,
        publicContract.provider
      );
      maxSupply = (await v7.maxSupply()).toNumber();
    } catch (error) {
      console.error('Error fetching maxSupply from contract:', error);
    }

    return { mintedCount, maxSupply };
  }

  const fetchNftAnalytics = async () => {
    setNftLoading(true);
    setNftError('');
    setNftData({
      totalTokens: ENV_MAX_SUPPLY,
      mintedCount: 0,
      lastMinted: [],
      lastTransfers: [],
      contractAddress: process.env.REACT_APP_DEPLOYED_NFT_CONTRACT || '',
    });

    try {
      // Get the actual minted count and live cap from the contract
      let mintedCount = 0;
      let totalTokens = ENV_MAX_SUPPLY;
      if (publicContract) {
        ({ mintedCount, maxSupply: totalTokens } = await fetchContractSupply());
      }

      // lastMinted
      const mintedResp = await fetch(`${baseUrl}/nfts/lastMinted`);
      const mintedDataRaw = mintedResp.ok ? await mintedResp.json() : [];
      let mintedData = Array.isArray(mintedDataRaw)
        ? mintedDataRaw
        : parseNftObjectOrArray(mintedDataRaw);

      // lastTransfers
      const transfersResp = await fetch(`${baseUrl}/nfts/lastTransfers`);
      const transfersDataRaw = transfersResp.ok
        ? await transfersResp.json()
        : [];
      let transfersData = Array.isArray(transfersDataRaw)
        ? transfersDataRaw
        : parseNftObjectOrArray(transfersDataRaw);

      // -----------------------------------------------------------
      // Perform the Sogni username lookups for minted and transfers
      // -----------------------------------------------------------
      // Look up minted => minted.to => minted.toUser
      mintedData = await Promise.all(
        mintedData.map(async (m) => {
          const user = await addressToSogniUsername(m.to);
          return { ...m, toUser: user };
        })
      );

      // For lastTransfers => from & to => fromUser, toUser
      transfersData = await Promise.all(
        transfersData.map(async (t) => {
          let fromUser = null;
          let toUser = null;
          if (
            t.from &&
            t.from !== '0x0000000000000000000000000000000000000000'
          ) {
            fromUser = await addressToSogniUsername(t.from);
          }
          if (
            t.to &&
            t.to !== '0x0000000000000000000000000000000000000000'
          ) {
            toUser = await addressToSogniUsername(t.to);
          }
          return { ...t, fromUser, toUser };
        })
      );

      // Final: set in local state
      setNftData({
        totalTokens,
        mintedCount,
        lastMinted: mintedData || [],
        lastTransfers: transfersData || [],
        contractAddress:
          process.env.REACT_APP_DEPLOYED_NFT_CONTRACT || '',
      });
    } catch (err) {
      console.error('NFT analytics fetch error:', err);
      setNftError(err.message);
    } finally {
      setNftLoading(false);
    }
  };

  useEffect(() => {
    if (activeKey === '1') {
      fetchNftAnalytics();
    } else {
      setNftLoading(false);
      setNftError('');
      setNftData({
        totalTokens: ENV_MAX_SUPPLY,
        mintedCount: 0,
        lastMinted: [],
        lastTransfers: [],
        contractAddress: process.env.REACT_APP_DEPLOYED_NFT_CONTRACT || '',
      });
    }
    // Make sure publicContract is in the dependency list
  }, [activeKey, baseUrl, publicContract]);

  // Filter out from=0x0000 for "transfers"
  const lastTransfersFiltered = nftData.lastTransfers.filter(
    (t) => t.from !== '0x0000000000000000000000000000000000000000'
  );

  // Helper to show an address + optional username
  const renderAddressLink = (addr, username) => {
    if (!addr) return '0x???';
    const short = `${addr.slice(0, 6)}...${addr.slice(-4)}`;
    // If we have a Sogni username, show "short (SogniUser)"
    const displayed = username ? `${short} (${username})` : short;

    return (
      <a
        href={`https://basescan.org/address/${addr}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: '#b5ff16', textDecoration: 'underline dotted' }}
      >
        {displayed}
      </a>
    );
  };

  // Add new useEffect for NFT analytics
  useEffect(() => {
    console.log('📊 NFT analytics useEffect triggered', { firstNftId, baseUrl });
    const fetchNftAnalytics = async () => {
      if (!firstNftId) return;

      try {
        console.log('📊 Fetching NFT analytics for', firstNftId);
        // Fetch daily analytics
        const dailyRes = await fetch(`https://api.sogni.ai/v1/analytics/day/nft/${firstNftId}`);
        if (!dailyRes.ok) throw new Error('Failed to fetch NFT daily analytics');
        const dailyData = await dailyRes.json();

        // Fetch yesterday analytics
        const yesterday = moment().utc().subtract(1, 'day').format('YYYY-MM-DD');
        const yesterdayRes = await fetch(`https://api.sogni.ai/v1/analytics/day/nft/${firstNftId}?date=${yesterday}`);
        let yesterdayData = null;
        if (yesterdayRes.ok) {
          yesterdayData = await yesterdayRes.json();
        }

        // Fetch lifetime analytics
        const lifetimeRes = await fetch(`https://api.sogni.ai/v1/analytics/lifetime/nft/${firstNftId}`);
        if (!lifetimeRes.ok) throw new Error('Failed to fetch NFT lifetime analytics');
        const lifetimeData = await lifetimeRes.json();

        setNftAnalytics({
          daily: dailyData.data?.analytics || {},
          yesterday: yesterdayData?.data?.analytics || {},
          lifetime: lifetimeData.data || {}
        });
        console.log('✅ NFT analytics loaded for', firstNftId);
      } catch (err) {
        console.error('NFT analytics fetch error:', err);
      }
    };

    fetchNftAnalytics();
  }, [firstNftId, baseUrl]);

  // Add useEffect to handle deep linking
  useEffect(() => {
    console.log('🔗 Deep linking useEffect triggered', { locationSearch: location.search, allWorkersLength: allWorkers.length });
    const params = new URLSearchParams(location.search);
    const nftId = params.get('nftId');
    
    // Only handle deep linking for owned NFTs (allWorkers), non-owned NFTs are handled in main useEffect
    if (nftId && allWorkers.length > 0) {
      const worker = allWorkers.find(w => w.nftId === parseInt(nftId));
      if (worker && (!selectedWorker || selectedWorker.nftId !== worker.nftId)) {
        console.log('🔄 Deep link: Selecting owned worker', worker.nftId);
        setActiveKey('0');
        setSelectedWorker(worker);
        setFirstNftId(worker.nftId);
        setGpuData(worker.data);
        setGpuError(''); // Clear any previous errors
      }
    }
  }, [location.search, allWorkers]); // Removed selectedWorker from dependencies

  // Add this useEffect for initial data loading only
  useEffect(() => {
    console.log('🚀 Initial data loading useEffect triggered', { firstNftId });
    if (firstNftId) {
      // Only fetch job history, not GPU stats (those are already loaded)
      console.log('📋 Initial: Calling fetchJobHistory for', firstNftId);
      fetchJobHistory(firstNftId); // Initial load of job history
      // Note: Removed refreshGpuStats() call to prevent duplicate API calls
    }
  }, [firstNftId]); // Only depend on firstNftId

  // Handle accordion section changes
  const handleAccordionSelect = (eventKey) => {
    const newKey = eventKey === activeKey ? null : eventKey;
    setActiveKey(newKey);

    // Update URL without triggering navigation/reload
    const params = new URLSearchParams(location.search);
    if (newKey === null) {
      params.delete('section');
    } else {
      params.set('section', newKey === '0' ? 'platform' : 'nft');
    }
    
    window.history.replaceState(
      {},
      '',
      `${location.pathname}?${params.toString()}`
    );
  };

  // Keep the worker selection handler
  const handleWorkerSelect = (worker) => {
    console.log('🎯 handleWorkerSelect called for worker:', worker.nftId);
    // Only update if we're actually selecting a different worker
    if (!selectedWorker || selectedWorker.nftId !== worker.nftId) {
      setSelectedWorker(worker);
      setGpuData(worker.data);
      setFirstNftId(worker.nftId);
      setGpuError(''); // Clear any previous errors
      
      // Update URL to reflect selected worker
      const params = new URLSearchParams(location.search);
      params.set('nftId', worker.nftId);
      navigate(`?${params.toString()}`, { replace: true });
    }
  };

  // Helper function to format last seen time for tooltips
  const formatLastSeenForTooltip = (lastSeen) => {
    if (!lastSeen) return null;
    
    const now = Date.now();
    const diffMs = now - parseInt(lastSeen, 10);
    if (diffMs <= 0) return null;

    const duration = moment.duration(diffMs);
    const days = Math.floor(duration.asDays());
    const hours = Math.floor(duration.asHours()) % 24;
    const minutes = Math.floor(duration.asMinutes()) % 60;

    if (days > 0) {
      return `${days} day${days !== 1 ? 's' : ''} ago`;
    } else if (hours > 0) {
      return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    } else if (minutes > 0) {
      return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    } else {
      return 'Just now';
    }
  };

  // Helper function to create tooltip content for NFT tabs
  const createTooltipContent = (workerData) => {
    const parts = [];
    
    if (workerData.image) {
      parts.push(`Worker: ${workerData.image}`);
    }
    
    if (workerData.gpu) {
      parts.push(`GPU: ${workerData.gpu}`);
    }
    
    if (workerData.id) {
      parts.push('Status: Online');
    } else {
      const lastSeenFormatted = formatLastSeenForTooltip(workerData.lastSeen);
      if (lastSeenFormatted) {
        parts.push(`Last seen: ${lastSeenFormatted}`);
      } else {
        parts.push('Status: Never connected');
      }
    }
    
    return parts.join('\n');
  };

  return (
    <div>
      {/* We rely on the .my-custom-accordion rules from App.css */}
      <Accordion
        className="my-custom-accordion"
        activeKey={activeKey}
        onSelect={handleAccordionSelect}
        defaultActiveKey=""
      >
        {/* Platform Analytics: eventKey="0" */}
        <Accordion.Item eventKey="0">
          <Accordion.Header>Platform Analytics</Accordion.Header>
          <Accordion.Body>
            {platformLoading && (
              <div>
                <Spinner animation="border" size="sm" /> Loading...
              </div>
            )}
            {!platformLoading && platformError && (
              <p style={{ color: 'red' }}>{platformError}</p>
            )}
            {!platformLoading && !platformError && platformData?.data && (
              <div>
                <p>
                  <strong>Total Artist Signups:</strong>{' '}
                  {formatNumber(platformData.data.usignupAddress)}
                </p>
                <p>
                  <strong>Total GPU Worker Signups:</strong>{' '}
                  {formatNumber(platformData.data.uloginWorkerAddress)}
                </p>
                <p>
                  <strong>Total images processed:</strong>{' '}
                  {formatNumber(platformData.data.jobCompleteWorker)}
                </p>
              </div>
            )}
          </Accordion.Body>
        </Accordion.Item>

        {/* NFT Analytics: eventKey="1" */}
        <Accordion.Item eventKey="1">
          <Accordion.Header>NFT Analytics</Accordion.Header>
          <Accordion.Body>
            {/* A small Refresh button */}
            <div className="d-flex justify-content-end mb-2">
              <Button variant="outline-success" size="sm" onClick={fetchNftAnalytics}>
                Refresh
              </Button>
            </div>

            {nftLoading && (
              <div>
                <Spinner animation="border" size="sm" /> Loading...
              </div>
            )}
            {!nftLoading && nftError && (
              <p style={{ color: 'red' }}>{nftError}</p>
            )}
            {!nftLoading && !nftError && (
              <div>
                <p>
                  <strong>Total NFTs in Collection:</strong>{' '}
                  {nftData.totalTokens}
                </p>
                <p>
                  <strong>Total NFTs Minted:</strong>{' '}
                  {nftData.mintedCount}
                </p>
                <div style={{ marginBottom: '15px' }}>
                  <strong>Links:</strong>{' '}
                  <a
                    href={`https://basescan.org/address/${nftData.contractAddress}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#b5ff16', textDecoration: 'underline dotted' }}
                  >
                    NFT Contract
                  </a>{' '}
                  |{' '}
                  <a
                    href={process.env.REACT_APP_SOGNI_OPENSEA_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#b5ff16', textDecoration: 'underline dotted' }}
                  >
                    OpenSea
                  </a>
                </div>

                <h5>Last 10 Minted NFTs:</h5>
                {nftData.lastMinted.length > 0 ? (
                  <Table striped bordered hover variant="dark" responsive>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Token ID</th>
                        <th>Owner</th>
                      </tr>
                    </thead>
                    <tbody>
                      {nftData.lastMinted.map((mint, i) => (
                        <tr key={i}>
                          <td>
                            {/* FIX: show the actual minted date, fromNow or formatted */}
                            <span title={moment(mint.date).format('YYYY-MM-DD HH:mm:ss')}>
                              {moment(mint.date).fromNow()}
                            </span>
                          </td>
                          <td>
                            <a
                              href={`https://basescan.org/nft/${nftData.contractAddress}/${mint.tokenId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: '#b5ff16', textDecoration: 'underline dotted' }}
                            >
                              {mint.tokenId}
                            </a>
                          </td>
                          <td>
                            {renderAddressLink(mint.to, mint.toUser)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <p>No minted NFTs found.</p>
                )}

                <h5 style={{ marginTop: '30px' }}>Last 10 NFT Transfers:</h5>
                {lastTransfersFiltered.length > 0 ? (
                  <Table striped bordered hover variant="dark" responsive>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Token ID</th>
                        <th>From</th>
                        <th>To</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lastTransfersFiltered.map((t, idx) => (
                        <tr key={idx}>
                          <td>
                            {/* FIX: show the actual transfer date */}
                            <span title={moment(t.date).format('YYYY-MM-DD HH:mm:ss')}>
                              {moment(t.date).fromNow()}
                            </span>
                          </td>
                          <td>
                            <a
                              href={`https://basescan.org/nft/${nftData.contractAddress}/${t.tokenId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: '#b5ff16', textDecoration: 'underline dotted' }}
                            >
                              {t.tokenId}
                            </a>
                          </td>
                          <td>{renderAddressLink(t.from, t.fromUser)}</td>
                          <td>{renderAddressLink(t.to, t.toUser)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <p>No transfers found.</p>
                )}
              </div>
            )}
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>

      {/* The "Links" block below the entire Accordion */}
      <div style={{ marginTop: '15px' }}>
        <strong>Links:</strong>{' '}
        <a
          href="https://www.sogni.ai/leaderboard"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#b5ff16', textDecoration: 'underline dotted' }}
        >
          Sogni Leaderboards
        </a>{' | '}
        <a
          href="https://docs.sogni.ai/"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#b5ff16', textDecoration: 'underline dotted' }}
        >
          Sogni Docs
        </a>{' | '}
        <a
          href="https://discord.sogni.ai/"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#b5ff16', textDecoration: 'underline dotted' }}
        >
          Discord
        </a>{' | '}
        <a
          href="https://t.me/sogniai"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#b5ff16', textDecoration: 'underline dotted' }}
        >
          Telegram
        </a>{' | '}
        <a
          href="https://twitter.com/sogni_protocol"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#b5ff16', textDecoration: 'underline dotted' }}
        >
          Twitter
        </a>{' | '}
        <a
          href="https://www.youtube.com/@SogniAI"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#b5ff16', textDecoration: 'underline dotted' }}
        >
          Youtube
        </a>
      </div>
    </div>
  );
};

// Helper for "X days/hours/minutes ago"
const calculateRelativeTime = (timestamp) => {
  if (!timestamp || timestamp === 'undefined' || timestamp === 'null' || timestamp === null) return '';
  const now = Date.now();
  const diffMs = now - parseInt(timestamp, 10);
  if (diffMs <= 0) return '';

  const duration = moment.duration(diffMs);
  const days = Math.floor(duration.asDays());
  const hours = Math.floor(duration.asHours()) % 24;
  const minutes = Math.floor(duration.asMinutes()) % 60;
  const seconds = Math.floor(duration.asSeconds()) % 60;

  const parts = [];
  if (days > 0) parts.push(`${days} day${days !== 1 ? 's' : ''}`);
  if (hours > 0) parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
  if (minutes > 0) parts.push(`${minutes} min${minutes !== 1 ? 's' : ''}`);
  if (seconds > 0) parts.push(`${seconds} sec${seconds !== 1 ? 's' : ''}`);

  // Only take the first two parts
  return parts && parts.length > 0 ? parts.slice(0, 2).join(', ') + ' ago' : '';
};

// Another helper that returns "X days/hours/minutes" (without "ago")
const calculateDurationSince = (timestamp) => {
  if (!timestamp) return 'N/A';
  const now = Date.now();
  const diffMs = now - parseInt(timestamp, 10);
  if (diffMs <= 0) return 'N/A';

  const duration = moment.duration(diffMs);
  const days = Math.floor(duration.asDays());
  const hours = Math.floor(duration.asHours()) % 24;
  const minutes = Math.floor(duration.asMinutes()) % 60;
  const seconds = Math.floor(duration.asSeconds()) % 60;

  const parts = [];
  if (days > 0) parts.push(`${days} day${days !== 1 ? 's' : ''}`);
  if (hours > 0) parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
  if (minutes > 0) parts.push(`${minutes} min${minutes !== 1 ? 's' : ''}`);
  if (seconds > 0) parts.push(`${seconds} sec${seconds !== 1 ? 's' : ''}`);

  // Only take the first two parts
  return parts && parts.length > 0 ? parts.slice(0, 2).join(', ') : '';
};

// Helper to calculate job duration in seconds
const calculateJobDuration = (endTime, startTime) => {
  if (!endTime || !startTime) return 'N/A';
  const duration = moment.duration(parseInt(endTime) - parseInt(startTime));
  return `${duration.asSeconds().toFixed(1)}s`;
};

export default NFTAnalyticsView;
